import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Plane, Building2, Home, Layers, Phone, MessageCircle, Loader2 } from "lucide-react";
import { LogoWordmark } from "./Logo";
import { useLang } from "../../i18n/LanguageProvider";
import { AGENCY } from "../../i18n/dict";

const ENGINE_BASE = "https://booking.traveltotime.com";

// Mapping of tab keys to labels + icons
const TABS = [
  { key: "flights", label_fr: "Vols", label_es: "Vuelos", label_en: "Flights", Icon: Plane },
  { key: "hotels", label_fr: "Hôtels", label_es: "Hoteles", label_en: "Hotels", Icon: Building2 },
  { key: "apartments", label_fr: "Appartements", label_es: "Apartamentos", label_en: "Apartments", Icon: Home },
  { key: "flight_and_hotel", label_fr: "Vol + Hôtel", label_es: "Vuelo + Hotel", label_en: "Flight + Hotel", Icon: Layers },
];

export const BookingPage = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const activeTab = params.get("tab") || "flights";
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [iframeKey, setIframeKey] = useState(0); // force reload on tab change

  useEffect(() => {
    setLoading(true);
    setIframeKey((k) => k + 1);
  }, [activeTab]);

  const setTab = (key) => {
    setParams({ tab: key });
  };

  const getLabel = (tab) => {
    if (lang === "es") return tab.label_es;
    if (lang === "en") return tab.label_en;
    return tab.label_fr;
  };

  // Deep-link URL — engine is a single page with anchor tabs, so we pass the tab as a fragment
  // The engine uses session-dependent IDs so we can't reliably deep-link into a specific tab,
  // but we pass a hash hint that the engine can optionally use.
  const iframeSrc = `${ENGINE_BASE}/?utm_source=traveltotimes&utm_medium=embed&utm_content=${activeTab}#tab=${activeTab}`;

  return (
    <div className="min-h-screen bg-[#0B1416] text-[#F5F1E8] flex flex-col">
      {/* Slim brand header */}
      <header
        data-testid="book-header"
        className="relative z-10 border-b border-[#F5F1E8]/10 bg-[#0B1416]"
      >
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-4">
          <button
            data-testid="book-back"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 shrink-0"
          >
            <ArrowLeft size={16} />
            <div className="hidden md:block">
              <LogoWordmark tone="light" />
            </div>
            <div className="md:hidden font-heading text-lg">Travel To Time</div>
          </button>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={`tel:${AGENCY.phone_raw}`}
              className="text-[11px] uppercase tracking-[0.28em] opacity-70 hover:opacity-100 transition-opacity flex items-center gap-2"
            >
              <Phone size={12} /> {AGENCY.phone}
            </a>
            <a
              href={`https://wa.me/${AGENCY.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              data-testid="book-whatsapp"
              className="tt-btn tt-btn-primary"
              style={{ padding: "10px 18px", fontSize: 11 }}
            >
              <MessageCircle size={12} /> WhatsApp
            </a>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-[1440px] mx-auto px-4 md:px-8 pb-1">
          <div
            className="flex items-center gap-1 overflow-x-auto scrollbar-none"
            style={{ scrollbarWidth: "none" }}
            data-testid="book-tabs"
          >
            {TABS.map((tab) => {
              const active = activeTab === tab.key;
              const Icon = tab.Icon;
              return (
                <button
                  key={tab.key}
                  data-testid={`book-tab-${tab.key}`}
                  onClick={() => setTab(tab.key)}
                  className={`relative flex items-center gap-2 px-4 md:px-6 py-3 md:py-4 text-[11px] uppercase tracking-[0.24em] font-medium whitespace-nowrap transition-colors ${
                    active
                      ? "text-[#0B1416]"
                      : "text-[#F5F1E8]/60 hover:text-[#F5F1E8]"
                  }`}
                >
                  {active && (
                    <motion.div
                      layoutId="book-tab-pill"
                      className="absolute inset-0 bg-[#6EE3E6] rounded-sm"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                  <Icon size={14} strokeWidth={1.4} className="relative z-10" />
                  <span className="relative z-10">{getLabel(tab)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Iframe frame */}
      <div className="relative flex-1 bg-[#F5F1E8]">
        <AnimatePresence>
          {loading && (
            <motion.div
              key="loader"
              initial={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-0 z-20 bg-[#0B1416] flex flex-col items-center justify-center text-[#F5F1E8]"
            >
              <Loader2 size={40} className="animate-spin text-[#6EE3E6]" strokeWidth={1.2} />
              <div className="mt-6 overline text-[#6EE3E6]">
                Chargement du moteur de réservation…
              </div>
              <div className="mt-2 text-xs opacity-50">
                Connexion sécurisée à booking.traveltotime.com
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <iframe
          key={iframeKey}
          ref={iframeRef}
          data-testid="booking-iframe"
          src={iframeSrc}
          title="Travel To Time — Booking Engine"
          onLoad={() => setLoading(false)}
          className="w-full h-full border-0 block"
          style={{ minHeight: "calc(100vh - 105px)" }}
          allow="clipboard-read; clipboard-write; payment; geolocation"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>

      {/* Footer strip */}
      <div className="text-center text-[10px] uppercase tracking-[0.28em] py-3 opacity-50 border-t border-[#F5F1E8]/10 bg-[#0B1416]">
        Réservation propulsée par Travel To Time · Paiement sécurisé
      </div>
    </div>
  );
};
